import React from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { faRedo } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/Button';
import { studioRenderFailureMessage } from './studioRenderState';

import './StudioRenderError.scss';

export interface StudioRenderErrorProps {
  /** Persisted failure code; picks the copy. Unknown or missing → generic. */
  failureCode?: string | null;
  /** Replaces the failure copy, for surfaces with their own wording. */
  message?: string;
  /** Shown only when provided; it must call the Studio re-render endpoint. */
  onRetry?: () => void;
  isRetrying?: boolean;
  retryDisabled?: boolean;
  /** Icon, text and Retry, for dropzones. The default is text and actions. */
  compact?: boolean;
  className?: string;
  /** Extra actions shown after Retry. */
  children?: React.ReactNode;
}

/** The shared failure block for a Studio render. */
const StudioRenderError: React.FC<StudioRenderErrorProps> = ({
  failureCode,
  message,
  onRetry,
  isRetrying = false,
  retryDisabled = false,
  compact = false,
  className,
  children,
}) => {
  const text = message ?? studioRenderFailureMessage(failureCode);
  const hasActions = !!onRetry || React.Children.count(children) > 0;
  // The block can sit inside a clickable upload dropzone or a link (a content
  // bank grid tile). Retry must not also open the file picker, and must not
  // follow the link: stopping propagation skips the link's handler, so the
  // default action has to be cancelled here too.
  const handleRetry = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    onRetry?.();
  };

  return (
    <div
      className={cx(
        'studio-render-error',
        { 'studio-render-error--compact': compact },
        className
      )}
    >
      {compact && (
        <FontAwesomeIcon
          className="studio-render-error__icon"
          icon={faExclamationTriangle}
          size="3x"
        />
      )}
      {compact ? (
        <small role="alert" className="studio-render-error__message">
          {text}
        </small>
      ) : (
        <p role="alert" className="studio-render-error__message">
          {text}
        </p>
      )}
      {hasActions && (
        <div className="studio-render-error__actions">
          {onRetry && (
            <Button
              className={cx('studio-render-error__retry', {
                'black-tertiary': compact,
              })}
              color={compact ? undefined : 'light'}
              outline={!compact}
              icon={<FontAwesomeIcon icon={faRedo} />}
              loading={isRetrying}
              disabled={retryDisabled || isRetrying}
              onClick={handleRetry}
              size="sm"
            >
              Retry
            </Button>
          )}
          {children}
        </div>
      )}
    </div>
  );
};

export default StudioRenderError;
